import { useState, useCallback, useRef, useEffect } from "react";
import { LiveRegionContext } from "./LiveRegionContext";

/**
 * LiveRegionProvider - Announces dynamic content changes to screen readers
 * WCAG 4.1.3 Status Messages (Level AA)
 *
 * Location: frontend/src/providers/LiveRegionProvider.jsx
 *
 * Setup in App.jsx:
 * ```jsx
 * import { LiveRegionProvider } from "@/providers/LiveRegionProvider";
 *
 * const App = () => {
 *   return (
 *     <LiveRegionProvider>
 *       /* Your app
 *     </LiveRegionProvider>
 *   );
 * };
 * ```
 *
 * Usage in components:
 * ```jsx
 * import { useAnnounce } from "@/hooks/useAnnounce";
 *
 * const MyComponent = () => {
 *   const { announce } = useAnnounce();
 *
 *   const handleSave = async () => {
 *     await saveData();
 *     announce("Document saved successfully");
 *   };
 * };
 * ```
 */
export const LiveRegionProvider = ({ children }) => {
  const [politeMessage, setPoliteMessage] = useState("");
  const [assertiveMessage, setAssertiveMessage] = useState("");
  const politeTimeoutRef = useRef(null);
  const assertiveTimeoutRef = useRef(null);

  const announce = useCallback((message, politeness = "polite") => {
    if (politeness === "assertive") {
      if (assertiveTimeoutRef.current) {
        clearTimeout(assertiveTimeoutRef.current);
      }
      setAssertiveMessage("");
      requestAnimationFrame(() => {
        setAssertiveMessage(message);
      });
      assertiveTimeoutRef.current = setTimeout(() => {
        setAssertiveMessage("");
      }, 1000);
    } else {
      if (politeTimeoutRef.current) {
        clearTimeout(politeTimeoutRef.current);
      }
      setPoliteMessage("");
      requestAnimationFrame(() => {
        setPoliteMessage(message);
      });
      politeTimeoutRef.current = setTimeout(() => {
        setPoliteMessage("");
      }, 1000);
    }
  }, []);

  useEffect(
    () => () => {
      if (politeTimeoutRef.current) {
        clearTimeout(politeTimeoutRef.current);
      }
      if (assertiveTimeoutRef.current) {
        clearTimeout(assertiveTimeoutRef.current);
      }
    },
    [],
  );

  return (
    <LiveRegionContext.Provider value={{ announce }}>
      {children}
      {/* Polite live region - for non-urgent updates */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {politeMessage}
      </div>
      {/* Assertive live region - for urgent updates */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {assertiveMessage}
      </div>
    </LiveRegionContext.Provider>
  );
};

export default LiveRegionProvider;
